/* ══════════════════════════════════════════════════════════════════
   Der Satz zu einem Stück, das ein anderer Vorgang vorgemerkt hat —
   ohne Abhängigkeiten, damit ein Test ihn ohne Browser prüft.

   Prüfrunde Interview F, N7-05 (05.10.2026): Vor „wird gerade vermietet"
   (F21) stand im Abschlussblatt *„AB-08 wäre frei, ist aber durch Angebot
   V-2026-0111 (PB-Ohne Mail) bis reserviert."* — V-2026-0111 war kein
   Angebot, sondern ein Auftrag, den eine Kollegin gerade abschloss, und
   hinter „bis" fehlte das Datum, weil die Vormerkung keine Frist trägt.
   Erst „Trotzdem nehmen" brachte den richtigen Satz, als Fehler der API.

   Jetzt:
   - **Abschluss läuft** (Entwurf ohne Angebot, jünger als die technische
     Grenze der API, `abschluss_laeuft`): gleich „wird gerade vermietet",
     **ohne** „Trotzdem nehmen" — die API nähme es ohnehin nicht.
   - **Auftrag im Abschluss** (Entwurf ohne Angebot, liegen geblieben):
     „durch den Auftrag im Abschluss …".
   - **Angebot**: wie bisher.
   - Ohne Frist fällt „bis …" weg.
   ══════════════════════════════════════════════════════════════════ */

export type Vormerkung = {
  vorgang?: string;
  kunde: string;
  /** Die Frist als Text (`tagKurz`), leer ohne Frist. */
  zusatz?: string;
  imAbschluss?: boolean;
  abschlussLaeuft?: boolean;
};

export type Reserviertsatz = {
  schluessel: string;
  werte: Record<string, string>;
  /** Darf „Trotzdem nehmen" angeboten werden? */
  nehmbar: boolean;
};

export function reserviertSatz(geraet: string, wer: Vormerkung): Reserviertsatz {
  const werte = { geraet, vorgang: wer.vorgang ?? "", kunde: wer.kunde, bis: wer.zusatz ?? "" };
  if (wer.abschlussLaeuft) return { schluessel: "schnell.geraetWirdVermietet", werte, nehmbar: false };
  const frist = Boolean(wer.zusatz);
  if (wer.imAbschluss) {
    return { schluessel: frist ? "schnell.geraetImAbschluss" : "schnell.geraetImAbschlussOhneFrist", werte, nehmbar: true };
  }
  return { schluessel: frist ? "schnell.geraetReserviert" : "schnell.geraetReserviertOhneFrist", werte, nehmbar: true };
}

/* ══════════════ Trefferliste und Statuszeile (N7-05 Rest, Runde 9) ══════════════

   Prüfrunde Runde 8: Das Blatt sagte richtig „wird gerade vermietet", die
   Trefferliste darüber aber „DURCH ANGEBOT RESERVIERT · 1 durch Angebot
   reserviert", aufgeklappt „Durch Angebot V-2026-0119 (PB-Ohne Mail) bis —
   reserviert", und die Statuszeile im Abschlussblatt „frei, aber durch ein
   Angebot reserviert — darunter „Trotzdem nehmen““, obwohl dort kein solcher
   Knopf stand. `GET /nachschlagen` zählt jetzt getrennt (`im_abschluss`,
   `abschluss_laeuft`); hier stehen die Wörter dazu, an einer Stelle. */

/** Die Zählung einer Typzeile aus `GET /nachschlagen`. */
export type Vormerkzahl = { frei: number; reserviert: number; imAbschluss?: number; abschlussLaeuft?: number };

/** Was eine Bestätigung („Trotzdem nehmen") freigeben kann — nicht, was gerade vermietet wird. */
export const nehmbarReserviert = (z: Vormerkzahl): number => Math.max(0, z.reserviert - (z.abschlussLaeuft ?? 0));

/** Das Wort der Typzeile, wenn kein Stück frei ist und nur Vormerkungen im Weg stehen — sonst `null`. */
export function vormerkWort(z: Vormerkzahl): string | null {
  if (z.frei > 0 || z.reserviert === 0) return null;
  if ((z.abschlussLaeuft ?? 0) === z.reserviert) return "lage.wirdVermietet";
  if ((z.imAbschluss ?? 0) === z.reserviert) return "lage.imAbschluss";
  return "lage.reserviert";
}

/** Die Zahlen hinter „2 von 3 frei" — je Art ein Teil, ohne die leeren. */
export function vormerkTeile(z: Vormerkzahl): { schluessel: string; zahl: number }[] {
  const imAbschluss = z.imAbschluss ?? 0;
  const laeuft = z.abschlussLaeuft ?? 0;
  return [
    { schluessel: "nachschlagen.reserviertZahl", zahl: z.reserviert - imAbschluss },
    { schluessel: "nachschlagen.imAbschlussZahl", zahl: imAbschluss - laeuft },
    { schluessel: "nachschlagen.wirdVermietetZahl", zahl: laeuft },
  ].filter((teil) => teil.zahl > 0);
}

/** Wer ein Stück hält — die aufgeklappte Zeile der Trefferliste und die Merkliste. */
export type Vormerkwer = { vorgang: string; kunde: string; frist: string; imAbschluss?: boolean; abschlussLaeuft?: boolean };

/**
 * Der Satz zum Stück in der aufgeklappten Trefferliste. `fristText` ist die
 * Frist, wie die Maske sie schreibt (`tagMitWochentag`) — ohne Frist fällt
 * „bis" weg.
 */
export function vormerkungWer(r: Vormerkwer, fristText: string): Reserviertsatz {
  const werte = { vorgang: r.vorgang, kunde: r.kunde, frist: fristText };
  if (r.abschlussLaeuft) return { schluessel: "nachschlagen.wirdVermietetWer", werte, nehmbar: false };
  const mitFrist = Boolean(r.frist && fristText);
  if (r.imAbschluss) {
    return { schluessel: mitFrist ? "nachschlagen.imAbschlussWer" : "nachschlagen.imAbschlussWerOhneFrist", werte, nehmbar: true };
  }
  return { schluessel: mitFrist ? "nachschlagen.reserviertWer" : "nachschlagen.reserviertWerOhneFrist", werte, nehmbar: true };
}

/**
 * Die Statuszeile eines Postens im Abschluss, wenn nicht genug frei ist, aber
 * Vormerkungen im Weg stehen. `null`: Dieser Fall liegt nicht vor.
 *
 * - Reicht es mit dem, was sich nehmen lässt → gelb, „… — darunter
 *   „Trotzdem nehmen““ (Angebot oder Auftrag im Abschluss).
 * - Fehlt es nur, weil ein Kollege gerade vermietet → rot, ohne den Knopf.
 */
export function abschlussBefund(
  z: Vormerkzahl,
  menge: number,
  uebergangen: boolean,
): { art: "acht" | "stopp"; schluessel: string; nehmbar: boolean } | null {
  if (z.frei >= menge || z.frei + z.reserviert < menge) return null;
  const nehmbar = nehmbarReserviert(z);
  if (z.frei + nehmbar >= menge) {
    const nurAbschluss = (z.imAbschluss ?? 0) - (z.abschlussLaeuft ?? 0) >= nehmbar;
    if (uebergangen) {
      return { art: "acht", schluessel: nurAbschluss ? "merkliste.imAbschlussGenommen" : "merkliste.reserviertGenommen", nehmbar: true };
    }
    return { art: "acht", schluessel: nurAbschluss ? "merkliste.nurImAbschluss" : "merkliste.nurReserviert", nehmbar: true };
  }
  return { art: "stopp", schluessel: "merkliste.wirdVermietet", nehmbar: false };
}
